// Review/approval UI for the AI playbook-ingestion pipeline. Lets an admin
// kick off a new AI draft (from a pasted/uploaded document, or generated
// from existing KB content), watch it move through the background
// pipeline's stages, then edit and approve the drafted roles before they're
// written into a real SalesPlaybook via POST /tasks/:id/sync.
//
// Reuses AddSalesPlaybookModal.tsx's SortableBulletItem/SortableQuestionItem
// (extracted into PlaybookFormSortableItems.tsx) for the same drag-reorder +
// tag-toggle editing interaction, rather than re-implementing it here.
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ProgressBar } from './ui/ProgressBar';
import { useConfirm } from './ui/ConfirmDialog';
import { Dropdown } from './ui/Dropdown';
import {
  Close as CloseIcon,
  Add as PlusIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudUpload as UploadIcon,
  HourglassEmpty as PendingIcon
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove
} from '@dnd-kit/sortable';
import {
  SortableBulletItem,
  SortableQuestionItem,
  SALES_PLAYBOOK_TAGS,
  TAG_LABELS
} from './PlaybookFormSortableItems';
import {
  createPlaybookIngestionTask,
  getPlaybookIngestionTasks,
  getPlaybookIngestionTask,
  retryPlaybookIngestionTask,
  deletePlaybookIngestionTask,
  savePlaybookIngestionDraft,
  syncPlaybookIngestionTask,
  replaceKnowledgeGeneratorStarterPlaybookFromUpload,
  replaceKnowledgeGeneratorStarterPlaybookWithManual,
  getPlaybookAudienceContext,
  getSalesPlaybooks,
  type PlaybookIngestionTask,
  type PlaybookIngestionStatus,
  type PlaybookIngestionDraftRole,
  type SalesPlaybookTag,
  type SalesPlaybook
} from '../services/apiService';

const IN_PROGRESS_STATUSES = new Set<PlaybookIngestionStatus>([
  'pending', 'locking_protected_content', 'extracting', 'generating', 'cleaning'
]);

const STATUS_LABELS: Record<PlaybookIngestionStatus, string> = {
  pending: 'Queued',
  locking_protected_content: 'Locking protected content',
  extracting: 'Extracting',
  generating: 'Generating',
  cleaning: 'Applying style pass',
  ready_for_review: 'Ready for review',
  synced: 'Published',
  error: 'Failed'
};

const PROTECTED_REASON_LABELS: Record<string, string> = {
  compliance_disclosure: 'Compliance disclosure',
  approved_claim: 'Approved claim',
  official_product_name: 'Official product name',
  can_say_cannot_say: 'Can say / cannot say',
  other: 'Other'
};

const StatusBadge: React.FC<{ status: PlaybookIngestionStatus }> = ({ status }) => {
  const styles: Record<PlaybookIngestionStatus, string> = {
    pending: 'kb-tone-neutral',
    locking_protected_content: 'kb-tone-info',
    extracting: 'kb-tone-info',
    generating: 'kb-tone-info',
    cleaning: 'kb-tone-info',
    ready_for_review: 'kb-tone-warning',
    synced: 'kb-tone-success',
    error: 'kb-tone-error'
  };
  return (
    <span className={`px-1.5 py-0.5 text-xs rounded-full ${styles[status]}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
};

interface ReviewRoleState {
  id: string;
  name: string;
  displayName: string;
  isActive: boolean;
  pitchBullets: string[];
  advancedBullets: string[];
  questions: { text: string; tags: SalesPlaybookTag[] }[];
  isExpanded: boolean;
}

type ReviewIssueKind = 'missing' | 'inferred' | 'clarification';
type ReviewIssueSection = 'pitch' | 'questions' | 'advanced';
interface ReviewIssue {
  id: string;
  kind: ReviewIssueKind;
  roleId: string;
  audience: string;
  section: ReviewIssueSection;
  index?: number;
  excerpt: string;
}

const INFERRED_MARKER = /\s*\[inferred[^\]]*\]\s*/ig;
const CLARIFICATION_MARKER = /\s*\[(?:needs|requires)[^\]]*clarification[^\]]*\]\s*/ig;
const cleanReviewMarker = (value: string) => value.replace(INFERRED_MARKER, ' ').replace(CLARIFICATION_MARKER, ' ').replace(/\s+/g, ' ').trim();
const normalizeAudienceKey = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'general';
const markerKind = (value: string): Exclude<ReviewIssueKind, 'missing'> | null => {
  INFERRED_MARKER.lastIndex = 0;
  CLARIFICATION_MARKER.lastIndex = 0;
  if (CLARIFICATION_MARKER.test(value)) return 'clarification';
  if (INFERRED_MARKER.test(value)) return 'inferred';
  return null;
};

const sectionLabel = (section: ReviewIssueSection) => ({
  pitch: 'Intro pitch',
  questions: 'Qualifying questions',
  advanced: 'Deal progression',
}[section]);

function deriveAudienceOptions(targetAudience: string, confirmedAudiences: string[], playbooks: SalesPlaybook[]): string[] {
  const contextOptions = targetAudience
    .split(/[\n,;]+/)
    .map((value) => value.trim().replace(/^and\s+/i, ''))
    .filter(Boolean);
  const publishedOptions = playbooks.flatMap((playbook) =>
    playbook.roles.map((role) => role.displayName || role.name)
  ).filter((value) => value && value.toLowerCase() !== 'general');
  const options = [...confirmedAudiences, ...contextOptions, ...publishedOptions]
    .filter((value, index, all) => all.findIndex((other) => other.toLowerCase() === value.toLowerCase()) === index)
    .slice(0, 8);
  return options.length ? options : ['General'];
}

function buildReviewIssues(roles: ReviewRoleState[]): ReviewIssue[] {
  const issues: ReviewIssue[] = [];
  // Mirrors the live-readiness contract in backend-tenant/services/kbContent.js.
  // A draft that passes here should not immediately publish into a known
  // "needs guidance" state.
  const requiredItems: Record<ReviewIssueSection, number> = { pitch: 2, questions: 3, advanced: 1 };
  roles.filter((role) => role.isActive).forEach((role) => {
    const sections: Array<[ReviewIssueSection, string[]]> = [
      ['pitch', role.pitchBullets],
      ['questions', role.questions.map((question) => question.text)],
      ['advanced', role.advancedBullets],
    ];
    sections.forEach(([section, values]) => {
      const populated = values.filter((value) => value.trim());
      if (populated.length < requiredItems[section]) {
        const missingCount = requiredItems[section] - populated.length;
        issues.push({
          id: `${role.id}-${section}-missing`, kind: 'missing', roleId: role.id,
          audience: role.displayName, section,
          excerpt: `Add ${missingCount} more item${missingCount === 1 ? '' : 's'} (${populated.length}/${requiredItems[section]} present)`,
        });
      }
      values.forEach((value, index) => {
        const kind = markerKind(value);
        if (kind) issues.push({
          id: `${role.id}-${section}-${index}-${kind}`,
          kind,
          roleId: role.id,
          audience: role.displayName,
          section,
          index,
          excerpt: cleanReviewMarker(value),
        });
      });
    });
  });
  return issues;
}

function draftRolesToEditState(
  roles: PlaybookIngestionDraftRole[]
): ReviewRoleState[] {
  return roles.map((r, idx) => ({
    id: `role-${idx}-${r.name}`,
    name: r.name,
    displayName: r.displayName,
    isActive: r.isActive !== false,
    pitchBullets: r.pitch.length > 0
      ? [...r.pitch].sort((a, b) => a.sortOrder - b.sortOrder).map(p => p.text)
      : [''],
    advancedBullets: r.advanced.length > 0
      ? [...r.advanced].sort((a, b) => a.sortOrder - b.sortOrder).map(a => a.text)
      : [''],
    questions: r.questions.length > 0
      ? [...r.questions].sort((a, b) => a.sortOrder - b.sortOrder).map(q => ({ text: q.questionText, tags: q.tags }))
      : [{ text: '', tags: ['situation'] as SalesPlaybookTag[] }],
    isExpanded: idx === 0
  }));
}

function editStateToDraftRoles(roles: ReviewRoleState[]): PlaybookIngestionDraftRole[] {
  return roles.map((r, idx) => ({
    name: r.name,
    displayName: r.displayName.trim() || r.name,
    sortOrder: idx,
    isActive: r.isActive,
    pitch: r.pitchBullets.filter(b => b.trim()).map((text, i) => ({ text, sortOrder: i })),
    advanced: r.advancedBullets.filter(b => b.trim()).map((text, i) => ({ text, sortOrder: i })),
    questions: r.questions
      .filter(q => q.text.trim())
      .map((q, i) => ({ questionText: q.text, tags: q.tags, sortOrder: i, isActive: true }))
  }));
}

interface PlaybookIngestionReviewProps {
  isOpen: boolean;
  onClose: () => void;
  onSynced: () => void; // caller refreshes its industries list after a successful sync
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
  initialTaskId?: string | null;
  /** The owning onboarding knowledge task. Uploads replace its generated
   * starter playbook instead of creating an unrelated playbook draft. */
  sourceKnowledgeTaskId?: string;
  /** The onboarding review is still an unpublished draft, so it cannot offer
   * a generation path whose only source is the published KB. */
  allowGenerateFromPublishedKb?: boolean;
  /** Open directly on one new-draft source instead of the draft history. */
  initialNewDraftMode?: 'upload' | 'generate' | 'manual';
  /** Standalone review publishes immediately; starter review only saves the
   * draft for the onboarding page's final Publish starter set action. */
  publicationMode?: 'standalone' | 'starter';
  includedInStarterSet?: boolean;
  onIncludedInStarterSetChange?: (included: boolean) => void | Promise<void>;
  /** Notifies onboarding when its starter draft is replaced or saved. */
  onDraftSaved?: (task: PlaybookIngestionTask) => void;
}

const PlaybookIngestionReview: React.FC<PlaybookIngestionReviewProps> = ({
  isOpen, onClose, onSynced, setError, setSuccess, initialTaskId,
  sourceKnowledgeTaskId,
  allowGenerateFromPublishedKb = true,
  initialNewDraftMode,
  publicationMode = 'standalone',
  includedInStarterSet = true,
  onIncludedInStarterSetChange,
  onDraftSaved,
}) => {
  const confirm = useConfirm();
  const isStarterReview = publicationMode === 'starter';
  const [tasks, setTasks] = useState<PlaybookIngestionTask[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<PlaybookIngestionTask | null>(null);
  const [showNewDraftForm, setShowNewDraftForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [existingPlaybooks, setExistingPlaybooks] = useState<SalesPlaybook[]>([]);
  const [audienceOptions, setAudienceOptions] = useState<string[]>(['General']);
  const [selectedAudiences, setSelectedAudiences] = useState<string[]>(['General']);
  const [customAudience, setCustomAudience] = useState('');
  const [publishReceipt, setPublishReceipt] = useState<{ name: string; roleCount: number; publishedAt: string } | null>(null);

  const [newDraftMode, setNewDraftMode] = useState<'upload' | 'generate' | 'manual' | null>(
    allowGenerateFromPublishedKb ? 'generate' : null
  );
  const [uploadText, setUploadText] = useState('');
  const [uploadDocName, setUploadDocName] = useState('');
  const [dealSizeHint, setDealSizeHint] = useState('');
  const [manualPlaybookName, setManualPlaybookName] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [playbookDisplayName, setPlaybookDisplayName] = useState('');
  const [roles, setRoles] = useState<ReviewRoleState[]>([]);
  const [conflictMode, setConflictMode] = useState<'merge' | 'replace' | 'create_new'>('merge');
  const [createNewDisplayName, setCreateNewDisplayName] = useState('');

  const listPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const detailPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const listPollInFlightRef = useRef(false);
  const detailPollInFlightRef = useRef(false);

  const fetchTasks = useCallback(async (includeReferenceData = true) => {
    try {
      setLoadingTasks(true);
      const taskRequest = isStarterReview && sourceKnowledgeTaskId
        ? getPlaybookIngestionTasks(1, 50, undefined, sourceKnowledgeTaskId)
        : initialTaskId
        ? getPlaybookIngestionTask(initialTaskId).then((task) => ({ data: [task] }))
        : getPlaybookIngestionTasks(1, 50);
      const [response, referenceData] = await Promise.all([
        taskRequest,
        includeReferenceData
          ? Promise.all([getSalesPlaybooks(1, 100), getPlaybookAudienceContext()])
          : Promise.resolve(null)
      ]);
      const allTasks = response.data || [];
      // Starter playbooks belong to their onboarding review. The general
      // Playbooks screen shows only standalone imports/generations so it
      // cannot accidentally become a second editor for a pre-seed draft.
      const fetched = isStarterReview && sourceKnowledgeTaskId
        ? allTasks.filter((task) => task.sourceKnowledgeTaskId === sourceKnowledgeTaskId)
        : initialTaskId
        ? allTasks.filter((task) => task._id === initialTaskId)
        : allTasks.filter((task) => !task.sourceKnowledgeTaskId);
      setTasks(fetched);
      if (referenceData) {
        const [playbooksResponse, audienceContext] = referenceData;
        setExistingPlaybooks(playbooksResponse.salesPlaybooks || []);
        const options = deriveAudienceOptions(audienceContext.targetAudience, audienceContext.playbookAudiences || [], playbooksResponse.salesPlaybooks || []);
        setAudienceOptions(options);
        setSelectedAudiences((current) => current.length === 1 && current[0] === 'General' ? options : current);
      }
      if (initialTaskId && fetched.some((task) => task._id === initialTaskId)) {
        setSelectedTaskId(initialTaskId);
        setShowNewDraftForm(false);
      }
      if (fetched.length === 0) {
        setShowNewDraftForm(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load playbook draft tasks');
    } finally {
      setLoadingTasks(false);
    }
  }, [initialTaskId, isStarterReview, setError, sourceKnowledgeTaskId]);

  useEffect(() => {
    if (isOpen) {
      if (initialNewDraftMode) {
        setSelectedTaskId(null);
        setNewDraftMode(initialNewDraftMode);
        setShowNewDraftForm(true);
      } else if (!initialTaskId) {
        setShowNewDraftForm(false);
      }
      fetchTasks();
    }
    // fetchTasks is rebuilt every render; this is a fetch-on-open effect only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const hasUnselectedInProgress = tasks.some(
    (task) => task._id !== selectedTaskId && IN_PROGRESS_STATUSES.has(task.status)
  );

  // Poll the queue while an unselected task is still processing. The selected
  // task is refreshed by the detail poll below.
  useEffect(() => {
    if (isOpen && hasUnselectedInProgress && !listPollRef.current) {
      listPollRef.current = setInterval(async () => {
        if (document.visibilityState === 'hidden' || listPollInFlightRef.current) return;
        listPollInFlightRef.current = true;
        try {
          await fetchTasks(false);
        } finally {
          listPollInFlightRef.current = false;
        }
      }, 3000);
    } else if ((!isOpen || !hasUnselectedInProgress) && listPollRef.current) {
      clearInterval(listPollRef.current);
      listPollRef.current = null;
    }
    return () => {
      if (listPollRef.current) {
        clearInterval(listPollRef.current);
        listPollRef.current = null;
      }
    };
  }, [isOpen, hasUnselectedInProgress, fetchTasks]);

  const loadTaskDetail = useCallback(async (taskId: string) => {
    try {
      const task = await getPlaybookIngestionTask(taskId);
      setSelectedTask(task);
      setTasks((current) => current.map((item) => item._id === task._id ? task : item));
      setPublishReceipt(null);
      if (task.status === 'ready_for_review') {
        setPlaybookDisplayName(task.draftPlaybookDisplayName);
        setRoles(draftRolesToEditState(task.draftRoles));
        setConflictMode('merge');
        setCreateNewDisplayName(`${task.draftPlaybookDisplayName} - New`);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load task');
    }
  }, [setError]);

  useEffect(() => {
    if (selectedTaskId) {
      loadTaskDetail(selectedTaskId);
    } else {
      setSelectedTask(null);
    }
    // loadTaskDetail is rebuilt every render; only selectedTaskId should retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTaskId]);

  const selectedTaskInProgress = Boolean(
    selectedTask && IN_PROGRESS_STATUSES.has(selectedTask.status)
  );

  // Poll the open task's detail while it's still processing.
  useEffect(() => {
    if (selectedTaskInProgress && selectedTaskId && !detailPollRef.current) {
      detailPollRef.current = setInterval(async () => {
        if (document.visibilityState === 'hidden' || detailPollInFlightRef.current) return;
        detailPollInFlightRef.current = true;
        try {
          await loadTaskDetail(selectedTaskId);
        } finally {
          detailPollInFlightRef.current = false;
        }
      }, 3000);
    } else if (!selectedTaskInProgress && detailPollRef.current) {
      clearInterval(detailPollRef.current);
      detailPollRef.current = null;
    }
    return () => {
      if (detailPollRef.current) {
        clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
    };
  }, [selectedTaskInProgress, selectedTaskId, loadTaskDetail]);

  const resetNewDraftForm = () => {
    setNewDraftMode(allowGenerateFromPublishedKb ? 'generate' : null);
    setUploadText('');
    setUploadDocName('');
    setSelectedAudiences(audienceOptions);
    setCustomAudience('');
    setDealSizeHint('');
    setManualPlaybookName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.txt')) {
      setError('Only plain-text (.txt) files are supported right now.');
      return;
    }
    setUploadDocName(file.name);
    const reader = new FileReader();
    reader.onload = () => setUploadText(String(reader.result || ''));
    reader.onerror = () => setError('Failed to read file');
    reader.readAsText(file);
  };

  const handleCreateTask = async () => {
    if (!newDraftMode) {
      setError('Choose how you want to start the playbook draft.');
      return;
    }
    if (newDraftMode === 'upload' && !uploadText.trim()) {
      setError('Paste or upload the source document text first');
      return;
    }
    if (newDraftMode === 'generate' && selectedAudiences.length === 0) {
      setError('Choose at least one buyer audience for this playbook.');
      return;
    }
    setCreating(true);
    try {
      const task = newDraftMode === 'upload'
        ? isStarterReview && sourceKnowledgeTaskId
          ? await replaceKnowledgeGeneratorStarterPlaybookFromUpload(
              sourceKnowledgeTaskId,
              uploadText,
              uploadDocName
            )
          : await createPlaybookIngestionTask({
              mode: 'upload',
              sourceText: uploadText,
              sourceDocumentName: uploadDocName
            })
        : newDraftMode === 'generate'
        ? await createPlaybookIngestionTask({
            mode: 'generate',
            audiences: selectedAudiences,
            dealSizeHint
          })
        : isStarterReview && sourceKnowledgeTaskId
        ? await replaceKnowledgeGeneratorStarterPlaybookWithManual(
            sourceKnowledgeTaskId,
            manualPlaybookName
          )
        : await createPlaybookIngestionTask({
            mode: 'manual',
            playbookName: manualPlaybookName
          });
      if (isStarterReview && (newDraftMode === 'upload' || newDraftMode === 'manual')) {
        onDraftSaved?.(task);
      }
      setSuccess(newDraftMode === 'manual' ? 'Blank draft ready to review.' : 'Draft started. This can take a minute or two.');
      resetNewDraftForm();
      setShowNewDraftForm(false);
      await fetchTasks();
      setSelectedTaskId(task._id);
    } catch (err: any) {
      setError(err.message || 'Failed to start draft');
    } finally {
      setCreating(false);
    }
  };

  const handleRetry = async (taskId: string) => {
    setActionBusy(true);
    try {
      await retryPlaybookIngestionTask(taskId);
      setSuccess('Task queued for retry');
      await fetchTasks();
      await loadTaskDetail(taskId);
    } catch (err: any) {
      setError(err.message || 'Failed to retry task');
    } finally {
      setActionBusy(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!(await confirm({
      title: 'Discard this draft?',
      message: 'This cannot be undone.',
      confirmLabel: 'Discard draft',
      cancelLabel: 'Keep draft',
      variant: 'destructive',
    }))) return;
    setActionBusy(true);
    try {
      await deletePlaybookIngestionTask(taskId);
      setSuccess('Draft discarded');
      if (selectedTaskId === taskId) {
        setSelectedTaskId(null);
      }
      await fetchTasks();
    } catch (err: any) {
      setError(err.message || 'Failed to discard task');
    } finally {
      setActionBusy(false);
    }
  };

  const normalizedPlaybookName = playbookDisplayName.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
  const existingPlaybook = existingPlaybooks.find((playbook) => playbook.playbookName === normalizedPlaybookName) || null;

  const reviewIssues = useMemo(() => buildReviewIssues(roles), [roles]);

  const handleApprove = async () => {
    if (!selectedTask) return;
    if (!playbookDisplayName.trim()) {
      setError('Playbook name is required');
      return;
    }
    const activeRoles = roles.filter(r => r.isActive);
    if (!isStarterReview && activeRoles.length === 0) {
      setError('At least one included role is required to approve');
      return;
    }
    if (existingPlaybook && conflictMode === 'create_new' && !createNewDisplayName.trim()) {
      setError('Enter a name for the separate playbook');
      return;
    }
    if (!isStarterReview && reviewIssues.length > 0) {
      setError(`Resolve ${reviewIssues.length} review item${reviewIssues.length === 1 ? '' : 's'} before publishing.`);
      return;
    }
    setSyncing(true);
    try {
      const draftRoles = editStateToDraftRoles(roles);
      if (isStarterReview) {
        const saved = await savePlaybookIngestionDraft(selectedTask._id, {
          expectedUpdatedAt: selectedTask.updatedAt,
          draftPlaybookName: playbookDisplayName,
          draftPlaybookDisplayName: playbookDisplayName,
          draftRoles,
        });
        setSelectedTask(saved);
        setTasks((current) => current.map((task) => task._id === saved._id ? saved : task));
        setSuccess('Playbook draft saved. Nothing is live until you publish.');
        onDraftSaved?.(saved);
        return;
      }
      const result = await syncPlaybookIngestionTask(selectedTask._id, {
        draftPlaybookName: playbookDisplayName,
        draftPlaybookDisplayName: playbookDisplayName,
        draftRoles,
        conflictMode: existingPlaybook ? conflictMode : undefined,
        createNewDisplayName: existingPlaybook && conflictMode === 'create_new'
          ? createNewDisplayName
          : undefined
      });
      const publishedAt = new Date().toISOString();
      const syncedTask = {
        ...selectedTask,
        status: 'synced' as PlaybookIngestionStatus,
        syncedPlaybookName: result.playbookName,
        syncedRoleIds: result.addedRoleIds,
        syncedAt: publishedAt,
      };
      setPublishReceipt({ name: result.playbookDisplayName, roleCount: result.addedRoleIds.length, publishedAt });
      setSelectedTask(syncedTask);
      setTasks((current) => current.map((task) => task._id === syncedTask._id ? syncedTask : task));
      setSuccess(`Published "${result.playbookDisplayName}".`);
      await fetchTasks();
      onSynced();
    } catch (err: any) {
      setError(err.message || 'Failed to publish draft');
    } finally {
      setSyncing(false);
    }
  };

  // ---- Role editing handlers (mirrors AddSalesPlaybookModal.tsx) ----

  const toggleRoleExpanded = (id: string) =>
    setRoles(rs => rs.map(r => (r.id === id ? { ...r, isExpanded: !r.isExpanded } : r)));

  const toggleRoleActive = (id: string) =>
    setRoles(rs => rs.map(r => (r.id === id ? { ...r, isActive: !r.isActive } : r)));

  const handleRoleNameChange = (id: string, displayName: string) =>
    setRoles(rs => rs.map(r => (r.id === id ? { ...r, name: normalizeAudienceKey(displayName), displayName } : r)));

  const handleAddPitchBullet = (id: string) =>
    setRoles(rs => rs.map(r => (r.id === id ? { ...r, pitchBullets: [...r.pitchBullets, ''] } : r)));

  const handleRemovePitchBullet = (id: string, index: number) =>
    setRoles(rs => rs.map(r =>
      r.id === id && r.pitchBullets.length > 1
        ? { ...r, pitchBullets: r.pitchBullets.filter((_, i) => i !== index) }
        : r
    ));

  const handlePitchBulletChange = (id: string, index: number, value: string) =>
    setRoles(rs => rs.map(r => {
      if (r.id !== id) return r;
      const bullets = [...r.pitchBullets];
      bullets[index] = value;
      return { ...r, pitchBullets: bullets };
    }));

  const handlePitchDragEnd = (id: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRoles(rs => rs.map(r =>
      r.id === id ? { ...r, pitchBullets: arrayMove(r.pitchBullets, active.id as number, over.id as number) } : r
    ));
  };

  const handleAddAdvancedBullet = (id: string) =>
    setRoles(rs => rs.map(r => (r.id === id ? { ...r, advancedBullets: [...r.advancedBullets, ''] } : r)));

  const handleRemoveAdvancedBullet = (id: string, index: number) =>
    setRoles(rs => rs.map(r =>
      r.id === id && r.advancedBullets.length > 1
        ? { ...r, advancedBullets: r.advancedBullets.filter((_, i) => i !== index) }
        : r
    ));

  const handleAdvancedBulletChange = (id: string, index: number, value: string) =>
    setRoles(rs => rs.map(r => {
      if (r.id !== id) return r;
      const bullets = [...r.advancedBullets];
      bullets[index] = value;
      return { ...r, advancedBullets: bullets };
    }));

  const handleAdvancedDragEnd = (id: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRoles(rs => rs.map(r =>
      r.id === id ? { ...r, advancedBullets: arrayMove(r.advancedBullets, active.id as number, over.id as number) } : r
    ));
  };

  const handleAddQuestion = (id: string) =>
    setRoles(rs => rs.map(r =>
      r.id === id
        ? { ...r, questions: [...r.questions, { text: '', tags: ['situation'] as SalesPlaybookTag[] }] }
        : r
    ));

  const handleRemoveQuestion = (id: string, index: number) =>
    setRoles(rs => rs.map(r =>
      r.id === id && r.questions.length > 1
        ? { ...r, questions: r.questions.filter((_, i) => i !== index) }
        : r
    ));

  const handleQuestionTextChange = (id: string, index: number, value: string) =>
    setRoles(rs => rs.map(r => {
      if (r.id !== id) return r;
      const questions = [...r.questions];
      questions[index] = { ...questions[index], text: value };
      return { ...r, questions };
    }));

  const handleQuestionTagToggle = (id: string, index: number, tag: SalesPlaybookTag) =>
    setRoles(rs => rs.map(r => {
      if (r.id !== id) return r;
      const questions = [...r.questions];
      const currentTags = questions[index].tags;
      if (currentTags.includes(tag)) {
        if (currentTags.length > 1) {
          questions[index] = { ...questions[index], tags: currentTags.filter(t => t !== tag) };
        }
      } else {
        questions[index] = { ...questions[index], tags: [...currentTags, tag] };
      }
      return { ...r, questions };
    }));

  const handleQuestionDragEnd = (id: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRoles(rs => rs.map(r =>
      r.id === id ? { ...r, questions: arrayMove(r.questions, active.id as number, over.id as number) } : r
    ));
  };

  if (!isOpen) return null;

  const issueFor = (roleId: string, section: ReviewIssueSection, index: number) =>
    reviewIssues.find((issue) => issue.roleId === roleId && issue.section === section && issue.index === index);
  const annotationFor = (issue?: ReviewIssue) => issue && issue.kind !== 'missing' ? {
    label: issue.kind === 'clarification' ? 'Needs clarification' : 'Inferred, verify',
    help: issue.kind === 'clarification'
      ? 'Verify or replace this statement. Editing it marks it resolved.'
      : 'Confirm this is true or edit it. Editing it marks it verified.',
    tone: issue.kind === 'clarification' ? 'danger' as const : 'warning' as const,
  } : undefined;
  const inputIdFor = (roleId: string, section: ReviewIssueSection, index: number) =>
    `draft-${roleId}-${section}-${index}`;
  const focusReviewIssue = (issue: ReviewIssue) => {
    setRoles((current) => current.map((role) => role.id === issue.roleId ? { ...role, isExpanded: true } : role));
    window.requestAnimationFrame(() => {
      document.getElementById(inputIdFor(issue.roleId, issue.section, issue.index ?? 0))?.focus();
    });
  };

  const renderRoleContent = (role: ReviewRoleState) => (
    <div className="space-y-4">
      {/* Intro Pitch */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-ink-secondary">Intro Pitch</label>
          <button
            type="button"
            onClick={() => handleAddPitchBullet(role.id)}
            className="text-signature hover:text-signature-strong text-xs flex items-center gap-1"
          >
            <PlusIcon style={{ fontSize: '0.875rem' }} />
            Add Intro Pitch
          </button>
        </div>
        <DndContext collisionDetection={closestCenter} onDragEnd={(event) => handlePitchDragEnd(role.id, event)}>
          <SortableContext items={role.pitchBullets.map((_, i) => i)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {role.pitchBullets.map((bullet, index) => (
                <SortableBulletItem
                  key={index}
                  id={index}
                  value={cleanReviewMarker(bullet)}
                  inputId={inputIdFor(role.id, 'pitch', index)}
                  annotation={annotationFor(issueFor(role.id, 'pitch', index))}
                  placeholder={`Pitch point ${index + 1}...`}
                  canRemove={role.pitchBullets.length > 1}
                  onChange={(value) => handlePitchBulletChange(role.id, index, value)}
                  onRemove={() => handleRemovePitchBullet(role.id, index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Qualifying Questions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-ink-secondary">Qualifying Questions</label>
          <button
            type="button"
            onClick={() => handleAddQuestion(role.id)}
            className="text-signature hover:text-signature-strong text-xs flex items-center gap-1"
          >
            <PlusIcon style={{ fontSize: '0.875rem' }} />
            Add Question
          </button>
        </div>
        <DndContext collisionDetection={closestCenter} onDragEnd={(event) => handleQuestionDragEnd(role.id, event)}>
          <SortableContext items={role.questions.map((_, i) => i)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {role.questions.map((question, index) => (
                <SortableQuestionItem
                  key={index}
                  id={index}
                  question={{ ...question, text: cleanReviewMarker(question.text) }}
                  inputId={inputIdFor(role.id, 'questions', index)}
                  annotation={annotationFor(issueFor(role.id, 'questions', index))}
                  index={index}
                  canRemove={role.questions.length > 1}
                  onTextChange={(value) => handleQuestionTextChange(role.id, index, value)}
                  onRemove={() => handleRemoveQuestion(role.id, index)}
                  onTagToggle={(tag) => handleQuestionTagToggle(role.id, index, tag)}
                  tagLabels={TAG_LABELS}
                  allTags={SALES_PLAYBOOK_TAGS}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Deal Progression */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-ink-secondary">Deal Progression</label>
          <button
            type="button"
            onClick={() => handleAddAdvancedBullet(role.id)}
            className="text-signature hover:text-signature-strong text-xs flex items-center gap-1"
          >
            <PlusIcon style={{ fontSize: '0.875rem' }} />
            Add Deal Progression
          </button>
        </div>
        <DndContext collisionDetection={closestCenter} onDragEnd={(event) => handleAdvancedDragEnd(role.id, event)}>
          <SortableContext items={role.advancedBullets.map((_, i) => i)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {role.advancedBullets.map((bullet, index) => (
                <SortableBulletItem
                  key={index}
                  id={index}
                  value={cleanReviewMarker(bullet)}
                  inputId={inputIdFor(role.id, 'advanced', index)}
                  annotation={annotationFor(issueFor(role.id, 'advanced', index))}
                  placeholder={`Deal progression point ${index + 1}...`}
                  canRemove={role.advancedBullets.length > 1}
                  onChange={(value) => handleAdvancedBulletChange(role.id, index, value)}
                  onRemove={() => handleRemoveAdvancedBullet(role.id, index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );

  const renderNewDraftForm = () => (
    <div className="space-y-4">
      <div>
        <h4 className="text-base font-semibold text-ink">Create your playbook</h4>
        <p className="text-xs text-ink-muted mt-1">Start with a draft. Nothing is live until you review and publish it.</p>
      </div>
      {allowGenerateFromPublishedKb && <button
        type="button"
        onClick={() => setNewDraftMode('generate')}
        className={`kb-choice-card w-full px-4 py-3 text-left flex items-center gap-3 ${newDraftMode === 'generate' ? 'is-selected' : ''}`}
      >
        <span className="flex-1">
          <span className="block text-sm font-semibold">Generate a playbook from your published KB</span>
          <span className="block mt-0.5 text-xs font-normal text-ink-secondary">
            Recommended. We draft the structure; you review it before publishing.
          </span>
        </span>
      </button>}
      <div>
        <p className="mb-2 text-xs font-medium text-ink-muted">
          {allowGenerateFromPublishedKb ? 'Other ways to start' : 'Choose how to start'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => setNewDraftMode('upload')}
          className={`kb-choice-card px-3 py-2 text-sm flex items-center justify-center gap-1.5 ${newDraftMode === 'upload' ? 'is-selected' : ''}`}
        >
          <UploadIcon style={{ fontSize: '1rem' }} />
          Use a .txt file
        </button>
        <button
          type="button"
          onClick={() => setNewDraftMode('manual')}
          className={`kb-choice-card px-3 py-2 text-sm flex items-center justify-center gap-1.5 ${newDraftMode === 'manual' ? 'is-selected' : ''}`}
        >
          <PlusIcon style={{ fontSize: '1rem' }} />
          Start with an empty template
        </button>
        </div>
      </div>

      {newDraftMode === 'upload' ? (
        <div className="space-y-3">
          <p className="text-xs text-ink-muted">
            {isStarterReview && 'This replaces the AI-generated starter playbook in this onboarding draft. '}
            Paste plain text below, or load a .txt file. PDF and DOCX files are not supported yet. Approved
            claims, and other sensitive language you paste in will be automatically detected and frozen so the AI
            never rewrites them.
          </p>
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Document name (optional)</label>
            <input
              type="text"
              value={uploadDocName}
              onChange={(e) => setUploadDocName(e.target.value)}
              placeholder="e.g., Cleaning Services Sales Guide.txt"
              className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature text-sm"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-ink-secondary">Source text *</label>
              <label className="text-signature hover:text-signature-strong text-xs cursor-pointer">
                Load .txt file
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".txt,text/plain"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            </div>
            <textarea
              value={uploadText}
              onChange={(e) => setUploadText(e.target.value)}
              rows={10}
              placeholder="Paste the extracted document text here..."
              className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature text-sm font-mono"
            />
            <p className="mt-1 text-xs text-ink-muted">{uploadText.length.toLocaleString()} characters</p>
          </div>
        </div>
      ) : newDraftMode === 'generate' ? (
        <div className="space-y-3">
          <p className="text-xs text-ink-muted">
            No source document needed. This uses only your published knowledge base and organization settings;
            unpublished onboarding changes are never pulled into a playbook silently.
          </p>
          <div className="rounded-lg border border-hairline p-3">
            <label className="block text-sm font-semibold text-ink">Who will reps be speaking to? *</label>
            <p className="mt-1 text-xs text-ink-muted">
              These buyer audiences come from your company context and published playbooks. The generator creates one tailored section for each selection.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {audienceOptions.map((audience) => {
                const selected = selectedAudiences.includes(audience);
                return <button
                  key={audience}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setSelectedAudiences((current) => selected
                    ? current.filter((value) => value !== audience)
                    : [...current, audience])}
                  className={`kb-filter-chip px-3 py-1.5 text-xs font-medium ${selected ? 'is-active' : ''}`}
                >
                  {audience}
                </button>;
              })}
            </div>
            <div className="mt-3 flex gap-2">
              <input
                type="text"
                maxLength={80}
                value={customAudience}
                onChange={(event) => setCustomAudience(event.target.value)}
                placeholder="Add another buyer audience"
                className="flex-1 px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature text-sm"
              />
              <button
                type="button"
                disabled={!customAudience.trim() || audienceOptions.length >= 8}
                onClick={() => {
                  const audience = customAudience.trim();
                  if (!audience) return;
                  setAudienceOptions((current) => current.some((value) => value.toLowerCase() === audience.toLowerCase()) ? current : [...current, audience].slice(0, 8));
                  setSelectedAudiences((current) => current.some((value) => value.toLowerCase() === audience.toLowerCase()) ? current : [...current, audience].slice(0, 8));
                  setCustomAudience('');
                }}
                className="rounded-md border border-ink-border px-3 py-2 text-sm text-ink disabled:opacity-50"
              >
                Add
              </button>
            </div>
            {selectedAudiences.length === 0 && <p className="mt-2 text-xs text-error">Choose at least one buyer audience.</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Deal context (optional)</label>
            <input
              type="text"
              value={dealSizeHint}
              onChange={(e) => setDealSizeHint(e.target.value)}
              placeholder="e.g., $5k-$20k annual contracts"
              className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature text-sm"
            />
            <p className="mt-1 text-xs text-ink-muted">Shapes examples and language; it does not create or rename buyer audiences.</p>
          </div>
        </div>
      ) : newDraftMode === 'manual' ? (
        <div className="space-y-3">
          <p className="text-xs text-ink-muted">
            Start with an empty private draft, fill in the useful sections, then publish when it is ready.
          </p>
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1">Playbook name (optional)</label>
            <input
              type="text"
              value={manualPlaybookName}
              onChange={(e) => setManualPlaybookName(e.target.value)}
              placeholder="My first playbook"
              className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature text-sm"
            />
          </div>
        </div>
      ) : (
        <p className="rounded-md border border-hairline bg-surface-subtle px-3 py-2 text-xs text-ink-secondary">
          Select a source above to begin a private draft.
        </p>
      )}

      <div className="flex justify-end gap-2 pt-2">
        {tasks.length > 0 && (
          <button
            type="button"
            onClick={() => setShowNewDraftForm(false)}
            className="kb-button kb-button-secondary px-4 py-2 text-sm"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          onClick={handleCreateTask}
          disabled={creating || !newDraftMode || (newDraftMode === 'generate' && selectedAudiences.length === 0)}
          className="kb-button kb-button-primary px-4 py-2 text-sm disabled:cursor-not-allowed"
        >
          {creating ? 'Creating...' : newDraftMode === 'generate' ? 'Generate draft' : 'Create draft'}
        </button>
      </div>
    </div>
  );

  const renderInProgress = (task: PlaybookIngestionTask) => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <RefreshIcon className="animate-spin text-info mb-3" style={{ fontSize: '2.5rem' }} />
      <p className="text-sm font-medium text-ink">{STATUS_LABELS[task.status]}</p>
      <p className="text-xs text-ink-muted mt-1">{task.progress?.message || 'Working...'}</p>
      <ProgressBar value={task.progress?.percentage ?? 0} tone="info" className="w-64 mt-4" />
      {task.status === 'pending' && (
        <button
          type="button"
          onClick={() => handleDeleteTask(task._id)}
          disabled={actionBusy}
          className="kb-button kb-button-secondary mt-6 px-3 py-1.5 text-xs"
        >
          Cancel draft
        </button>
      )}
    </div>
  );

  const renderErrorState = (task: PlaybookIngestionTask) => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <ErrorIcon className="text-error mb-3" style={{ fontSize: '2.5rem' }} />
      <p className="text-sm font-medium text-ink">Draft failed</p>
      <p className="text-xs text-error mt-1 max-w-sm">{task.error?.message || 'Unknown error'}</p>
      {task.error?.step && <p className="text-xs text-ink-muted mt-0.5">Failed at: {task.error.step}</p>}
      <div className="flex gap-2 mt-6">
        <button
          type="button"
          onClick={() => handleDeleteTask(task._id)}
          disabled={actionBusy}
          className="kb-button kb-button-secondary px-3 py-1.5 text-xs"
        >
          Discard
        </button>
        {(task.error?.retryCount || 0) < 3 && (
          <button
            type="button"
            onClick={() => handleRetry(task._id)}
            disabled={actionBusy}
            className="kb-button kb-button-primary px-3 py-1.5 text-xs"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );

  const renderSyncedState = (task: PlaybookIngestionTask) => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <CheckCircleIcon className="text-success mb-3" style={{ fontSize: '2.5rem' }} />
      <p className="text-sm font-medium text-ink">Playbook published</p>
      <p className="text-xs text-ink-muted mt-1">
        Playbook: <span className="font-medium text-ink-secondary">{task.draftPlaybookDisplayName}</span>
        {task.syncedRoleIds?.length ? `, ${task.syncedRoleIds.length} buyer audience${task.syncedRoleIds.length === 1 ? '' : 's'} added` : ''}
      </p>
      <p className="kb-status kb-tone-success mt-2 text-xs font-medium" role="status" aria-live="polite">
        Persisted to live playbooks at {new Date(publishReceipt?.publishedAt || task.syncedAt || Date.now()).toLocaleTimeString()}
      </p>
      <button
        type="button"
        onClick={() => handleDeleteTask(task._id)}
        disabled={actionBusy}
        className="kb-button kb-button-secondary mt-6 px-3 py-1.5 text-xs"
      >
        Remove from queue
      </button>
    </div>
  );

  const renderReview = (task: PlaybookIngestionTask) => (
    <div className="space-y-4">
      <div className={`kb-notice ${reviewIssues.length ? 'kb-tone-warning' : 'kb-tone-success'}`} aria-label="Playbook review issues">
        <div className="flex items-start gap-2">
          {reviewIssues.length ? <WarningIcon className="mt-0.5 text-warning" style={{ fontSize: '1.1rem' }} /> : <CheckCircleIcon className="mt-0.5 text-success" style={{ fontSize: '1.1rem' }} />}
          <div className="flex-1">
            <p className="text-sm font-semibold text-ink">
              {reviewIssues.length ? `${reviewIssues.length} review item${reviewIssues.length === 1 ? '' : 's'} before publishing` : 'All draft checks resolved'}
            </p>
            {reviewIssues.length ? (
              <ul className="mt-2 space-y-2">
                {reviewIssues.map((issue) => (
                  <li key={issue.id} className="flex items-start justify-between gap-3 rounded-md border border-warning bg-surface px-3 py-2 text-xs text-ink-secondary">
                    <span>
                      <strong>{issue.kind === 'missing' ? 'Missing' : issue.kind === 'clarification' ? 'Needs clarification' : 'Inferred, verify'}</strong>
                      {': '}{issue.audience}, {sectionLabel(issue.section)}{issue.index === undefined ? '' : ` #${issue.index + 1}`}
                      <span className="mt-0.5 block text-ink-muted">{issue.excerpt}</span>
                    </span>
                    <button type="button" className="font-medium text-signature hover:text-signature-strong" onClick={() => focusReviewIssue(issue)}>Review</button>
                  </li>
                ))}
              </ul>
            ) : <p className="mt-1 text-xs text-success">Every included audience has at least 2 pitch points, 3 discovery questions, 1 deal-progression point, and no unresolved AI markers.</p>}
          </div>
        </div>
      </div>
      {task.itemErrors && task.itemErrors.length > 0 && (
        <div className="p-3 bg-warning-tint border border-warning rounded-md text-sm text-ink">
          <div className="flex items-start gap-2">
            <WarningIcon style={{ fontSize: '1.1rem' }} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium">
                {task.itemErrors.length} item{task.itemErrors.length !== 1 ? 's' : ''} may not have gotten the full style pass.
              </p>
              <details className="mt-1">
                <summary className="cursor-pointer text-xs text-warning">Show details</summary>
                <ul className="mt-1 space-y-1 text-xs text-ink-secondary list-disc list-inside">
                  {task.itemErrors.map((e, i) => (
                    <li key={i}>
                      {e.roleName ? `${e.roleName}: ` : ''}{e.section ? `${e.section}: ` : ''}{e.message || 'Unknown issue'}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          </div>
        </div>
      )}

      {task.protectedSpans && task.protectedSpans.length > 0 && (
        <div className="p-3 bg-info-tint border border-info rounded-md text-sm">
          <div className="flex items-center gap-2 text-info font-medium mb-2">
            <LockIcon style={{ fontSize: '1.1rem' }} />
            Protected content ({task.protectedSpans.length}), frozen and kept exactly as written
          </div>
          <ul className="space-y-2">
            {task.protectedSpans.map((span, i) => (
              <li key={i} className="flex items-start gap-2">
                <LockIcon style={{ fontSize: '0.85rem' }} className="text-info mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-ink">&ldquo;{span.text}&rdquo;</p>
                  <p className="text-xs text-info mt-0.5">{PROTECTED_REASON_LABELS[span.reason] || span.reason}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isStarterReview && (
        <label className="flex items-start gap-3 rounded-lg border border-info bg-info-tint px-4 py-3 text-sm text-ink">
          <input
            type="checkbox"
            aria-label="Include playbook in starter set"
            checked={includedInStarterSet}
            disabled={syncing}
            onChange={(event) => onIncludedInStarterSetChange?.(event.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-ink-border text-signature"
          />
          <span>
            <span className="block font-semibold text-ink">Include playbook in starter set</span>
            <span className="mt-0.5 block text-xs text-ink-secondary">
              Saving here keeps this as a draft. It becomes live only with Publish starter set.
            </span>
          </span>
        </label>
      )}

      <div>
        <label className="block text-sm font-medium text-ink-secondary mb-1">Playbook name *</label>
        <input
          type="text"
          value={playbookDisplayName}
          onChange={(e) => setPlaybookDisplayName(e.target.value)}
          className="w-full px-3 py-2 border border-ink-border rounded-md focus:outline-none focus:ring-2 focus:ring-signature"
        />
      </div>

      {existingPlaybook && !isStarterReview && (
        <div className="border border-warning bg-warning-tint rounded-md p-3">
          <p className="text-sm font-medium text-ink">A playbook with this name already exists</p>
          <p className="text-xs text-ink-secondary mt-1">
            Preview: {existingPlaybook.roles.length} existing section{existingPlaybook.roles.length === 1 ? '' : 's'};
            this draft has {roles.filter((role) => role.isActive).length}.
          </p>
          <div className="mt-3 space-y-2">
            {[
              ['merge', 'Merge this draft into the existing playbook'],
              ['replace', 'Replace the existing playbook with this draft'],
              ['create_new', 'Keep both as separate playbooks'],
            ].map(([value, label]) => (
              <label key={value} className="flex items-start gap-2 text-sm text-ink-secondary">
                <input
                  type="radio"
                  name="playbook-conflict"
                  value={value}
                  checked={conflictMode === value}
                  onChange={() => setConflictMode(value as 'merge' | 'replace' | 'create_new')}
                  className="mt-0.5"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          {conflictMode === 'create_new' && (
            <input
              type="text"
              value={createNewDisplayName}
              onChange={(event) => setCreateNewDisplayName(event.target.value)}
              aria-label="Separate playbook name"
              className="mt-3 w-full px-3 py-2 border border-ink-border rounded-md bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-signature"
            />
          )}
        </div>
      )}

      <div className="space-y-3">
        {roles.map(role => (
          <div
            key={role.id}
            className={`border rounded-lg overflow-hidden ${role.isActive ? 'border-ink-border' : 'border-hairline bg-surface-subtle opacity-70'}`}
          >
            <div
              className="bg-surface-subtle px-3 py-2 flex items-center justify-between cursor-pointer"
              onClick={() => toggleRoleExpanded(role.id)}
            >
              <div className="flex items-center gap-2 flex-1">
                {role.isExpanded ? (
                  <ExpandLessIcon className="text-ink-muted" style={{ fontSize: '1.25rem' }} />
                ) : (
                  <ExpandMoreIcon className="text-ink-muted" style={{ fontSize: '1.25rem' }} />
                )}
                <div className="flex-1" onClick={(event) => event.stopPropagation()}>
                  <span id={`buyer-audience-${role.id}-label`} className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-ink-muted">Buyer audience</span>
                  <Dropdown
                    labelledBy={`buyer-audience-${role.id}-label`}
                    width="field"
                    value={role.displayName}
                    onChange={(audience) => handleRoleNameChange(role.id, audience)}
                    options={[role.displayName, ...audienceOptions]
                      .filter((value, index, all) => all.indexOf(value) === index)
                      .map((audience) => ({ value: audience, label: audience }))}
                  />
                </div>
              </div>
              {!isStarterReview && <button
                type="button"
                className={`kb-filter-chip ml-2 px-3 py-1.5 text-xs font-medium ${role.isActive ? '' : 'is-active'}`}
                onClick={(event) => { event.stopPropagation(); toggleRoleActive(role.id); }}
                aria-pressed={role.isActive}
              >
                {role.isActive ? 'Exclude from playbook' : 'Include in playbook'}
              </button>}
            </div>
            {role.isExpanded && (
              <div className="px-3 pb-3 pt-3 bg-white">
                {renderRoleContent(role)}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-hairline">
        {!isStarterReview && <button
          type="button"
          onClick={() => handleDeleteTask(task._id)}
          disabled={actionBusy || syncing}
          className="kb-button kb-button-secondary px-4 py-2 text-sm"
        >
          Discard
        </button>}
        <button
          type="button"
          onClick={handleApprove}
          disabled={syncing || (!isStarterReview && reviewIssues.length > 0)}
          className="kb-button kb-button-primary px-4 py-2 text-sm disabled:cursor-not-allowed"
        >
          {isStarterReview
            ? (syncing ? 'Saving...' : 'Save draft')
            : (syncing ? 'Publishing...' : 'Publish playbook')}
        </button>
      </div>
      {!isStarterReview && reviewIssues.length > 0 && (
        <p className="text-right text-xs text-warning">Resolve the review items above to publish this playbook.</p>
      )}
    </div>
  );

  const renderDetail = () => {
    if (showNewDraftForm) return renderNewDraftForm();
    if (!selectedTask) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center text-ink-muted">
          <PendingIcon style={{ fontSize: '2rem' }} className="text-ink-disabled mb-2" />
          <p className="text-sm">Select a draft from the list, or start a new one.</p>
        </div>
      );
    }
    if (IN_PROGRESS_STATUSES.has(selectedTask.status)) return renderInProgress(selectedTask);
    if (selectedTask.status === 'error') return renderErrorState(selectedTask);
    if (selectedTask.status === 'synced') return renderSyncedState(selectedTask);
    if (selectedTask.status === 'ready_for_review') return renderReview(selectedTask);
    return null;
  };

  return (
    <div className="apexia-kb kb-modal-overlay fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-8 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
          <div className="absolute inset-0"></div>
        </div>

        <div role="dialog" aria-modal="true" aria-labelledby="playbook-drafts-title" className="modal-panel kb-dialog inline-block h-[94vh] w-full max-w-5xl align-bottom text-left transform transition-all sm:my-8 sm:h-auto sm:max-h-[90vh] sm:align-middle">
          {/* Header */}
          <div className="bg-surface px-4 pt-4 pb-3 border-b border-hairline">
            <div className="flex justify-between items-center">
              <div>
                <h2 id="playbook-drafts-title" className="heading-4 kb-dialog-title">Playbook drafts</h2>
                <p className="text-xs text-ink-muted mt-0.5">Create, review, and publish. Nothing is live before you publish it.</p>
              </div>
              <button type="button" aria-label="Close playbook drafts" onClick={onClose} className="min-h-[44px] min-w-[44px] rounded-lg text-ink-muted hover:bg-surface-subtle hover:text-ink-secondary focus:outline-none focus:ring-2 focus:ring-signature">
                <CloseIcon />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex h-[calc(94vh-73px)] min-w-0 flex-col sm:h-[70vh] sm:flex-row">
            {/* Queue */}
            <div className="max-h-48 w-full flex-shrink-0 border-b border-hairline bg-surface-subtle flex flex-col sm:max-h-none sm:w-64 sm:border-b-0 sm:border-r">
              <div className="p-3 border-b border-hairline flex items-center justify-between">
                <h4 className="text-sm font-semibold text-ink-secondary">Drafts</h4>
                <button
                  type="button"
                  onClick={() => { setSelectedTaskId(null); setShowNewDraftForm(true); }}
                  className="kb-icon-button"
                  title="New draft"
                >
                  <PlusIcon style={{ fontSize: '1.1rem' }} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {loadingTasks && tasks.length === 0 ? (
                  <div className="p-4 text-sm text-ink-muted text-center">Loading...</div>
                ) : tasks.length === 0 ? (
                  <div className="p-4 text-sm text-ink-muted text-center">No drafts yet.</div>
                ) : (
                  tasks.map(task => (
                    <button
                      key={task._id}
                      type="button"
                      onClick={() => { setSelectedTaskId(task._id); setShowNewDraftForm(false); }}
                      className={`kb-queue-item w-full text-left px-3 py-2.5 border-b border-hairline hover:bg-surface transition-colors ${!showNewDraftForm && selectedTaskId === task._id ? 'is-selected' : ''}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-ink truncate">
                          {task.draftPlaybookDisplayName || (task.mode === 'upload' ? (task.sourceDocumentName || '.txt draft') : task.mode === 'manual' ? 'Blank draft' : 'Generated draft')}
                        </span>
                        {IN_PROGRESS_STATUSES.has(task.status) && (
                          <RefreshIcon className="animate-spin text-info flex-shrink-0" style={{ fontSize: '0.9rem' }} />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <StatusBadge status={task.status} />
                        <span className="text-xs text-ink-muted">{task.mode === 'upload' ? '.txt' : task.mode === 'manual' ? 'Manual' : 'Generated'}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Detail */}
            <div className="min-w-0 flex-1 overflow-y-auto p-4">
              {renderDetail()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaybookIngestionReview;
